import { useEffect, useState, type ReactElement } from "react";
import { Dialog, ProgressBar } from "../ui/kit/index.ts";
import type { PageConfirms } from "./logout-everywhere.tsx";
import { say } from "../words.ts";
import { STAY_DIALOG_WIDTH, STAY_MARGIN_MS, secondsLeft, stayNote, ticksToMs } from "./menu/leave-words.ts";

/**
 * The dialog a page shows while its character stays in the world after a chosen leave (P2.3-T4, ADR-084's amendment
 * of 2026-10-13, item 10): the seconds left, counted down from the gateway's answer, over a bar that empties with
 * them, and no control, since the character has already left the player's hands. It closes itself when the stay has run out, a little after the map
 * server's own end of it, so character select does not meet the account's place still held.
 */

/**
 * How often the dialog reads the clock, in milliseconds.
 */
const STAY_READ_MS = 250;

/**
 * The bar's width in logical pixels, the design's for a leaving dialog's bar.
 */
const STAY_BAR_WIDTH = 180;

/**
 * The dialog's answer to Escape, which does nothing: the stay cannot be dismissed.
 */
function nothing(): void {
  return;
}

/**
 * The stay's dialog.
 *
 * @param props `ticks`, the stay's length; `done`, called once when it has run out; `now`, the page's clock.
 * @returns The dialog.
 */
export function Staying({ ticks, done, now = Date.now }: { readonly ticks: number; readonly done: () => void; readonly now?: () => number }): ReactElement {
  const [until] = useState(() => now() + ticksToMs(ticks));
  const [left, setLeft] = useState(() => until - now());
  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = until - now();
      setLeft(remaining);
      if (remaining <= -STAY_MARGIN_MS) {
        clearInterval(timer);
        done();
      }
    }, STAY_READ_MS);
    return () => clearInterval(timer);
  }, [until, now, done]);
  return (
    <Dialog
      title={say("menu.leave.stayTitle")}
      width={STAY_DIALOG_WIDTH}
      onClose={nothing}
      actions={<ProgressBar label={say("menu.leave.stayBar")} value={Math.max(0, left)} max={ticksToMs(ticks)} width={STAY_BAR_WIDTH} />}
    >
      <p style={{ margin: 0 }} role="status" aria-live="polite" data-leave-stay={secondsLeft(left)}>
        {stayNote(secondsLeft(left))}
      </p>
    </Dialog>
  );
}

/**
 * Shows the stay's dialog over the page until the stay has run out.
 *
 * @param confirms The page's dialogs.
 * @param ticks The stay's length.
 * @returns A promise that settles when the stay has run out, or at once when another dialog is open; it never
 *   rejects.
 */
export async function showStay(confirms: Pick<PageConfirms, "show">, ticks: number): Promise<void> {
  await confirms.show((done) => <Staying ticks={ticks} done={() => done(true)} />);
}
